import { TFunction } from "core/i18n";
import { fn } from "core/util";
import { formatBirthNumber } from "core/formatters";
import {
    Attachment,
    ChangeSubject,
    FileItem,
    FileItemsMap,
    FileItemStatusEnum,
    FundsTransferType,
    ServiceHubBankAccount,
    SubjectTypeEnum,
    VehicleCancellationReason,
} from "types";

export const getCancellationDateTitle = (t: TFunction, cancellationReason: VehicleCancellationReason): string => {
    return t(`vehicle.change.contractCancellation.dateTitle.${cancellationReason}`);
};

export const formatBankAccount = (t: TFunction, fundsTransferType: string, fundsTransferBankAccount: ServiceHubBankAccount) => {
    if (fn.isEmpty(fundsTransferType) || fn.isEmpty(fundsTransferBankAccount)) {
        return "";
    }

    if (fundsTransferType === FundsTransferType.INSURANCE_PAYMENT_BANK_ACCOUNT) {
        return t("change.fundsTransfer.type.insurancePaymentBankAccount");
    }

    if (!!fundsTransferBankAccount.iban) {
        return fundsTransferBankAccount.iban;
    }

    const accountPrefix = fundsTransferBankAccount.prefix ? `${fundsTransferBankAccount.prefix}-` : "";
    return `${accountPrefix}${fundsTransferBankAccount.accountNumber}/${fundsTransferBankAccount.bankCode}`;
};

export const formatSubjectData = (t: TFunction, person: ChangeSubject) => {
    if (person.subjectType?.toUpperCase() === SubjectTypeEnum.INDIVIDUAL) {
        return `${person.firstName} ${person.lastName}, ${t("holder.birthNumber")}: ${formatBirthNumber(person.birthNumber)}`;
    }

    return `${person.companyName}, ${t("holder.companyID")}: ${person.companyIdentificationNumber}`;
};

export const formatPhoneAndEmail = (t: TFunction, person: ChangeSubject) => {
    return `${t("holder.tel")}: ${person.phoneNumber}, ${t("holder.email")}: ${person.email}`;
};

export const formatAttachments = (attachments: Attachment[]) => {
    return attachments.map((item: Attachment) => item.label).join(", ");
};

export const getIsAnyFileItemPending = (items: FileItem[]) => {
    if (!items) {
        return false;
    }

    const pendingFiles = items.filter((item: FileItem) => item.uploadStatus === FileItemStatusEnum.PENDING);
    return pendingFiles.length > 0;
};

export const getIsSuccessUploadStateInEveryFileItem = (items: FileItem[]) => {
    if (!items || !items.length) {
        return true;
    }

    const successStates = [FileItemStatusEnum.DATA_IN_FORM, FileItemStatusEnum.UPLOADED];

    const incorrectFileItems = items.filter((item: FileItem) => !successStates.includes(item.uploadStatus));
    return incorrectFileItems.length === 0;
};

export const isAllAttachmentsUploaded = (attachments: Attachment[], uploadedAttachments: FileItemsMap) => {
    // Check if all required attachment has at min one file uploaded.
    const missingAttachments = attachments
        ?.filter((attachment) => !attachment?.isOptional)
        ?.map((requiredAttachment) => uploadedAttachments[requiredAttachment.type]?.length > 0)
        ?.filter((item) => !item);

    // Check that no attachment is in other that UPLOADED state.
    const pendingAttachments = attachments
        ?.map((attachment) => getIsSuccessUploadStateInEveryFileItem(uploadedAttachments[attachment.type]))
        ?.filter((item) => !item);

    return missingAttachments?.length === 0 && pendingAttachments?.length === 0;
};
